import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import { Toggle } from '@/components/admin/FormComponents';
import { cccdAdmin, type CatalogItemCategory } from '@/lib/api/cccdAdminClient';
import { toastError } from '@/lib/toast';
import { BoxCubeIcon } from '@/icons';
import { LoadState } from "@/components/ui/LoadState"
import { EmptyState } from "@/components/ui/EmptyState"

const CATEGORY_LABELS: Record<CatalogItemCategory, string> = {
  structure: 'Estructuras',
  electronics: 'Electrónica',
  launch_equipment: 'Equip. de lanzamiento',
  merchandising: 'Merchandising',
};

// Habilitación por evento (nacional o regional, resuelta del lado del
// backend por el mismo :id) del catálogo global de construcción/decoración
// (cccd-team-supply-catalog) — entidad separada de EventCategoriesSection's
// addons de inscripción. Solo el admin del catálogo global
// (CatalogItemsPage) edita nombre/precio; aquí solo se activa/desactiva para
// este evento puntual.
export function EventCatalogItemsSection({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const itemsQuery = useQuery({
    queryKey: ['supply-catalog-items'],
    queryFn: () => cccdAdmin.listCatalogItems(),
  });
  const linksQuery = useQuery({
    queryKey: ['event-catalog-items', eventId],
    queryFn: () => cccdAdmin.getEventCatalogItems(eventId),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['event-catalog-items', eventId] });

  const enableMutation = useMutation({
    mutationFn: (catalogItemId: string) => cccdAdmin.enableEventCatalogItem(eventId, catalogItemId),
    onSuccess: invalidate,
    onError: (err: any) => toastError(err?.response?.data?.error ?? 'No se pudo habilitar el artículo.'),
  });

  const disableMutation = useMutation({
    mutationFn: (catalogItemId: string) => cccdAdmin.disableEventCatalogItem(eventId, catalogItemId),
    onSuccess: invalidate,
    onError: (err: any) => toastError(err?.response?.data?.error ?? 'No se pudo deshabilitar el artículo.'),
  });

  const activeItems = (itemsQuery.data ?? []).filter((item) => item.isActive);
  const enabledIds = new Set((linksQuery.data ?? []).map((link) => link.catalogItemId));

  return (
    <section>
      <SectionHeader
        title="Catálogo de construcción"
        subtitle="Artículos del catálogo global disponibles para los equipos de este evento en su dashboard de Academy — edita nombre/precio desde Catálogo de construcción."
        icon={<BoxCubeIcon />}
        className="mb-5"
      >
        {itemsQuery.isLoading || linksQuery.isLoading ? (
          <LoadState />
        ) : activeItems.length === 0 ? (
          <EmptyState message="No hay artículos activos en el catálogo global todavía." />
        ) : (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
            {activeItems.map((item) => {
              const enabled = enabledIds.has(item.id);
              const pending = enableMutation.isPending || disableMutation.isPending;
              return (
                <div key={item.id} className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3 last:border-b-0 dark:border-gray-800">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{item.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{CATEGORY_LABELS[item.category]}</p>
                  </div>
                  <Toggle
                    checked={enabled}
                    disabled={pending}
                    onChange={() => (enabled ? disableMutation.mutate(item.id) : enableMutation.mutate(item.id))}
                    label=""
                  />
                </div>
              );
            })}
          </div>
        )}
      </SectionHeader>
    </section>
  );
}
